import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  processAndUploadPhotos,
  processAndUploadSingleFile,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'

// Import Bahela assets
import loadingImage from '/assets/samu/samu-wait.webp'
import cogoToast from '@successtar/cogo-toast'

interface UserContext {
  user?: {
    email: string
  }
}

const SelectPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [remainingTime, setRemainingTime] = useState<number>(120)
  const [isProcessing, setIsProcessing] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const uploadStartedRef = useRef<boolean>(false)
  const printJobSentRef = useRef<boolean>(false)
  const isMountedRef = useRef(true)
  const context = useOutletContext<UserContext | null>()

  // Simple loading state management
  const [isPreviewReady, setIsPreviewReady] = useState(false)

  const { id, setSessionState, gallery, galleryKey, userPreferences } =
    useSessionStateStore()

  // Call all hooks at the top level
  const cachedLoadingImage = useCachedBackgroundImage(loadingImage)
  const { config: globalConfig, getFrame, frames } = useConfigStore()
  const { downloadLocal } = useSettingsStore()
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const [isOnline, setIsOnline] = useState(true)

  // Initialize countdown hook
  const selectCountdown = useCountdown(120)

  const validPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  // Get frame configuration
  const selectedFrame = getFrame(userPreferences.frameId || frames[0]._id)

  // add but still unique for each .type. so photo-1 and photo-1 if occurs twice count as 1
  const maxSelectablePhotos = selectedFrame?.drawParams.images
    ? new Set(
        selectedFrame.drawParams.images
          .filter((image) => image.type.startsWith('photo'))
          .map((image) => image.type)
      ).size
    : 3

  const frameConfig = selectedFrame
    ? {
        id: selectedFrame._id,
        baseImage: selectedFrame.thumbnail,
        drawParams: selectedFrame.drawParams
      }
    : {
        id: 'preview',
        baseImage: '',
        drawParams: {
          canvasWidth: 213,
          canvasHeight: 652,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  useEffect((): (() => void) => {
    isMountedRef.current = true
    selectCountdown.setOnTick(() => {
      setRemainingTime(selectCountdown.getRemainingSeconds())
    })

    selectCountdown.setOnComplete(() => {
      handleContinue()
    })

    selectCountdown.start()
    stateUploader.uploadSessionState()

    // Upload photos and generate GIF when component mounts
    const initializeMedia = async (): Promise<void> => {
      // Prevent concurrent upload attempts
      if (uploadStartedRef.current) {
        return
      }
      uploadStartedRef.current = true

      try {
        const ctx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline
        }
        const photoItems = validPhotos.map((item) => ({ photoUrl: item.photoUrl }))
        try {
          await Promise.race([
            processAndUploadPhotos(photoItems, ctx),
            new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Photo uploads timed out')), 15000)
            )
          ])
          const g = useSessionStateStore.getState().gallery
          setSessionState({
            gallery: {
              ...g,
              individualPhotosUploaded: true,
              individualPhotosUploadedAt: Date.now()
            }
          })
        } catch {
          cogoToast.warn('Photo uploads timed out, continuing with partial results')
        }

        if (userPreferences.useRecording) {
          const recordingItem = gallery.capturedItems.find((item) => item.recordingUrl)
          if (recordingItem?.recordingUrl) {
            const res = await fetch(recordingItem.recordingUrl)
            const blob = await res.blob()
            const recordingFile = new File([blob], `dreambooth-${gallery.id}-recording.mp4`, { type: 'video/mp4' })
            await processAndUploadSingleFile(recordingFile, 'video', ctx)
          }
        }

        if (userPreferences.useGIF) {
          try {
            const photoUrls = validPhotos.map((item) => item.photoUrl)
            const gifSpeed = globalConfig?.pages.capture.settings.gifSpeed || 1000
            const customGifFrame = globalConfig?.pages.capture.settings.customGifFrame
            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              ctx,
              customGifFrame
            )
            const newItems: Array<{
              id: string
              animationUrl: string
              animationType: 'gif' | 'mp4'
              gifUrl: string
              photoUrl: string
              timestamp: Date
            }> = []
            if (mp4LocalUrl) {
              newItems.push({
                id: `dreambooth-animation-${gallery.id}`,
                animationUrl: mp4LocalUrl,
                animationType: 'mp4',
                gifUrl: '',
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (gifLocalUrl) {
              newItems.push({
                id: `dreambooth-animation-gif-${gallery.id}`,
                animationUrl: gifLocalUrl,
                animationType: 'gif',
                gifUrl: gifLocalUrl,
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (newItems.length > 0) {
              setSessionState({
                gallery: {
                  capturedItems: [...gallery.capturedItems, ...newItems]
                }
              })
            } else if (isOnline) {
              cogoToast.error('Failed to upload animation')
            }
          } catch (err) {
            console.warn('GIF/MP4 creation/upload failed', err)
          }
        }
      } catch (error) {
        console.error('Error initializing media:', error)
      }
    }

    initializeMedia()

    return () => {
      isMountedRef.current = false
      selectCountdown.stop()
    }
  }, [])

  // Check internet connectivity for offline detection
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then(setIsOnline)

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        setIsOnline(status)
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  // Wait for PrintPreview to be ready
  useEffect(() => {
    const waitForPreview = async (): Promise<void> => {
      if (previewRef.current) {
        try {
          await previewRef.current.waitForReady()
          setIsPreviewReady(true)
        } catch (error) {
          console.error('Error waiting for preview to be ready:', error)
          // Fallback: enable after a timeout
          setTimeout(() => {
            setIsPreviewReady(true)
          }, 5000)
        }
      }
    }
    waitForPreview()
  }, [selectedPhotos, frameConfig])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isProcessing) return

    setSelectedPhotos((prev) => {
      if (prev.includes(photoUrl)) {
        return prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        return [...prev, photoUrl]
      }
      return prev
    })
  }

  const handleContinue = async (): Promise<void> => {
    if (isProcessing) return

    // Check if preview is ready, show warning if not
    if (!isPreviewReady) {
      cogoToast.warn('Preview is still loading. Please wait...')
      return
    }

    if (selectedPhotos.length < maxSelectablePhotos) {
      // Auto-select remaining photos
      const unselectedPhotos = validPhotos
        .filter((item) => !selectedPhotos.includes(item.photoUrl))
        .slice(0, maxSelectablePhotos - selectedPhotos.length)
        .map((item) => item.photoUrl)

      setSelectedPhotos((prev) => [...prev, ...unselectedPhotos])

      // Wait for state update before proceeding
      await new Promise((resolve) => setTimeout(resolve, 1000))
    }

    setIsProcessing(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    try {
      // Create and handle strip preview
      const stripBlob = await previewRef.current?.exportImage()
      if (stripBlob) {
        const stripCtx: ProcessingContext = {
          context,
          projectId,
          sessionId: id || '',
          galleryId: galleryKey || gallery.id,
          downloadLocal,
          isOnline
        }

        if (isPrintingEnabled && !printJobSentRef.current) {
          didAttemptPrint = true
          printJobSentRef.current = true
          const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
          try {
            await initiatePrint(
              stripBlob,
              printerName,
              userPreferences.copies,
              settings,
              isOnline
            )
          } catch {
            // Logged in printService
          }
        }

        const stripResult = await processAndUploadStrip(stripBlob, stripCtx)
        const localUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)

        setSessionState({
          gallery: {
            capturedItems: [
              ...gallery.capturedItems,
              {
                id: `dreambooth-strip-${gallery.id}`,
                stripUrl: localUrl,
                photoUrl: '',
                timestamp: new Date()
              }
            ]
          }
        })
        if (!stripResult?.uploadedUrl && isOnline) {
          cogoToast.error('Failed to upload photo strip')
        }
      }

      stateUploader.uploadSessionState()
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      setIsProcessing(false)
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error in processing:', error)
      cogoToast.error(
        'Terjadi kesalahan dalam pemrosesan hasil sesi foto. Silakan laporkan masalah ini.'
      )
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
      setIsProcessing(false)
    }
  }

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-select.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo.png')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedBackground})` }}
    >
      <img
        alt="Samu Logo"
        src={cachedLogo}
        width={200}
        height={200}
        className="absolute bottom-0 left-6"
      />

      {/* Loading Overlay */}
      {isProcessing && (
        <div
          className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center"
          style={{ backgroundImage: `url(${cachedLoadingImage})`, backgroundSize: 'cover' }}
        ></div>
      )}

      {/* Main Content */}
      <div className="absolute top-[86px] left-[220px] flex flex-col gap-[51px]">
        <div className="text-left relative w-auto"></div>

        {/* Photo Grid */}
        <div className="w-full h-[601px] grid grid-cols-3 gap-[69px] mt-32">
          {validPhotos.map((item, index) => {
            const isSelected = selectedPhotos.includes(item.photoUrl)
            return (
              <div key={index} className="relative w-[269px] h-[269px]">
                <div
                  className={`absolute inset-0 overflow-hidden ${
                    isSelected ? 'border-[8px] rounded-xl border-orangePop' : ''
                  }`}
                >
                  <img
                    src={item.photoUrl}
                    className={`w-full h-full object-cover cursor-pointer btn-shadow`}
                    onClick={() => handlePhotoClick(item.photoUrl)}
                    alt={`Captured ${index + 1}`}
                  />
                </div>
                {isSelected && (
                  <div className="absolute top-4 right-4 drop-shadow-2xl w-[34px] h-[34px] rounded-full border-2 bg-grey border-orangePop flex items-center justify-center">
                    <span className="text-orangePop text-xl">
                      {selectedPhotos.indexOf(item.photoUrl) + 1}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Right Panel */}
      <div className="absolute top-0 right-0 py-[100px] px-[100px] h-full flex flex-col items-center justify-center gap-[30px]">
        <div className="relative">
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos}
            className="w-[400px] object-contain"
            sessionId={galleryKey || gallery.id}
          />
        </div>
        <div className="flex flex-col items-center gap-4 mt-8">
          <button
            onClick={handleContinue}
            disabled={
              selectedPhotos.length < maxSelectablePhotos || isProcessing || !isPreviewReady
            }
            className={`rounded-lg font-body-samu tracking-widest shadow-2xl text-nightBrown px-12 py-6 text-5xl hover:brightness-110 duration-200 active:brightness-110 ${
              selectedPhotos.length < maxSelectablePhotos || isProcessing || !isPreviewReady
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-juicyMango'
            }`}
          >
            Cetak
          </button>
          <span className="text-4xl font-body-samu text-foundationWhite">
            {formatTime(remainingTime) || '...'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default SelectPage
